import { useCallback, useMemo } from 'react';
import { gqlProfileStatusEnum as PROFILE_STATUS } from '@minecraft.graphql-types';
import { signUpQueryParams } from '@minecraft.utils';
import routerHistory from 'utils/router-history';
import { getMarketingRedirect, removeMarketingRedirect } from '@blocs.auth';
import { useHistory } from 'react-router';
import { PaywallPreviewSubscriptionPurchaseQuery as PURCHASE } from '@minecraft.graphql-operations';

type RouterFunc<T extends any[]> = (...args: T) => { link: string; state?: Record<string, any> };

const getRouterQuery = (isFromOnboarding: boolean): Record<string, string | Record<string, boolean>> => {
  return isFromOnboarding ? { linkQuery: `?isFromOnboarding=${isFromOnboarding}`, urlQuery: { isFromOnboarding } } : {};
};

/**
 *
 * the RouteConstructor class takes a function which returns the url for the route you intend it to route to
 * the function can take any number of arguments, which are passed into the function when it is called
 * the function must return an object with a `link` property which is the url for the route
 * it can also optionally return a `state` property which is an object that will be set into the router state
 * constructor - Instance should be created with function as param which will
 * used for route data, ex `new RouteConstructor((ipcProjectId) => ({
 *  link: `/ipc/project/${ipcProjectId}/role/create`,
 * }))`;
 * */
class RouteConstructor<T extends any[]> {
  func: RouterFunc<T>;

  constructor(func: RouterFunc<T>) {
    this.func = func;
  }

  /** build returns the entire result of the route function, including the state object
   * you should not need to use this directly, but it is used by the `useRouteHelper` push
   * function to get the state object to set into the router history
   * */
  build(...args: T): { link: string; state?: Record<string, any> } {
    return this.func(...args);
  }

  /** returns the url for the specified route, can be passed into
   * the `push` from the useHistory hook to fire the route change
   * or set into the `to` prop of the Link component
   */
  link(...args: T): string {
    return this.func(...args).link;
  }

  /** @deprecated use the `useRouteHelper` hook to handle routing in the talent app
   * expample usage:
   * const { push, link } = useRouteHelper();
   * instead of routeHelper.castingBillboard.redirect(true) do push('castingBillboard')
   * instead of routeHelper.castingBillboard.redirect(false) do link('castingBillboard')
   * */
  linkOrRedirect(redirect: boolean, ...args: T): string {
    const generated = this.func(...args);

    if (redirect) {
      routerHistory.push(generated.link);

      return null;
    }

    return generated.link;
  }

  /** @deprecated use the `useRouteHelper` hook to handle routing in the talent app
   * expample usage:
   * const { push, link } = useRouteHelper();
   * instead of routeHelper.castingBillboard.redirect(true) do push('castingBillboard')
   * */
  redirect(...args: T): void {
    const generated = this.func(...args);

    if (generated.state) {
      routerHistory.push({ pathname: generated.link, state: generated.state });
    } else {
      routerHistory.push(generated.link);
    }
  }

  /** this is still here for legacy reasons but ideally
   * we should not need this, all it does is wrap the generatend `link` URL
   * into an object with a `to` property that was usually used to spread
   * into Link components
   */
  forLink(...args: T): { to: string } {
    const generated = this.func(...args);

    return {
      to: generated.link,
    };
  }
}

const routeHelper = {
  acceptProfileAccess: new RouteConstructor((profileAccessToken: string) => ({
    link: `/accept-profile-access/${profileAccessToken}`,
  })),
  root: new RouteConstructor(() => ({
    link: '/',
  })),
  publicProfile: new RouteConstructor((guid: string) => ({
    link: `/public-profile/${guid}`,
  })),
  login: new RouteConstructor((queryParams?: URLSearchParams) => ({
    link: `/login?${queryParams ? queryParams.toString() : ''}`,
  })),
  project: new RouteConstructor((projectId: string | number) => ({
    link: `/project/${projectId}`,
  })),
  projectRole: new RouteConstructor(
    (projectId: string | number, roleId: string | number, queryParams?: URLSearchParams) => ({
      link: `/project/${projectId}/role/${roleId}?${queryParams ? queryParams.toString() : ''}`,
    })
  ),
  talentDisambiguation: new RouteConstructor(() => ({
    link: `/talents`,
  })),
  talentSettings: new RouteConstructor((artistId: number, { state = {} } = {}) => ({
    link: `/talents/${artistId}`,
    state,
  })),
  talentCopy: new RouteConstructor((profileId: string, { state = {} } = {}) => ({
    link: `/profiles/${profileId}/copy`,
    state,
  })),
  talentCopyFromRole: new RouteConstructor(
    (profileId: string, projectId: string, roleId: string, { state = {} } = {}) => ({
      link: `/project/${projectId}/role/${roleId}/profiles/${profileId}/copy`,
      state,
    })
  ),
  talentCopySelect: new RouteConstructor((profileId: string, { state = {} } = {}) => ({
    link: `/profiles/${profileId}/copy-select`,
    state,
  })),
  talentCopySelectFromRole: new RouteConstructor(
    (profileId: string, projectId: string, roleId: string, { state = {} } = {}) => ({
      link: `/project/${projectId}/role/${roleId}/profiles/${profileId}/copy-select`,
      state,
    })
  ),
  profileSubscriptions: new RouteConstructor(
    (profileId: string, { linkQuery = '' }: { linkQuery?: string } = {}, queryParams?: URLSearchParams) => ({
      link: `/profile/${profileId}/subscriptions${linkQuery}?${queryParams ? queryParams.toString() : ''}`,
      state: { originalPath: window.location.pathname },
    })
  ),
  profileSubscriptionSummary: new RouteConstructor(
    (profileId: string, planId: string, queryParams?: URLSearchParams) => ({
      link: `/profile/${profileId}/subscriptions/${planId}?${queryParams ? queryParams.toString() : ''}`,
    })
  ),
  profileCreateCheckout: new RouteConstructor(
    (profileId: string, purchase: PURCHASE, queryParams?: URLSearchParams) => ({
      link: `/profile/${profileId}/checkout?${queryParams ? queryParams.toString() : ''}`,
      state: { purchase },
    })
  ),
  profilePurchaseSuccess: new RouteConstructor((profileId: string) => ({
    link: `/profile/${profileId}/purchase-success`,
  })),
  talentCreate: new RouteConstructor(() => ({
    link: `/talents/create`,
  })),
  talentNameSettings: new RouteConstructor((artistId: number | string) => ({
    link: `/talents/${artistId}/name`,
  })),
  artistDisambiguation: new RouteConstructor(() => ({
    link: `/artists`,
  })),
  artistSelect: new RouteConstructor((artistId: number | string) => ({
    link: `/artists/${artistId}`,
  })),
  artistProfiles: new RouteConstructor((artistId: number | string) => ({
    link: `/artists/${artistId}/profiles`,
  })),
  artistCreate: new RouteConstructor(() => ({
    link: '/artists/create',
  })),
  artistCreatePersonal: new RouteConstructor(() => ({
    link: '/artists/create-personal',
  })),
  profileVerifyAge: new RouteConstructor((queryParams?: URLSearchParams) => {
    return {
      link: `/profile/verify-age?${queryParams ? queryParams.toString() : ''}`,
    };
  }),
  artistProfile: new RouteConstructor(() => ({
    link: `/profile`,
  })),
  profileCreate: new RouteConstructor((profileId?: string, queryParams?: URLSearchParams) => ({
    link: profileId
      ? `/profile/create/${profileId}?${queryParams ? queryParams.toString() : ''}`
      : `/profile/create?${queryParams ? queryParams.toString() : ''}`,
  })),
  profileUploadVideo: new RouteConstructor((profileId: string) => ({
    link: `/profile/${profileId}/upload-video`,
  })),
  profileUploadAudio: new RouteConstructor((profileId: string) => ({
    link: `/profile/${profileId}/upload-audio`,
  })),
  profileUploadPhoto: new RouteConstructor((profileId: string) => ({
    link: `/profile/${profileId}/upload-photo`,
  })),
  profile: new RouteConstructor((profileId: string, { linkQuery = '' }: { linkQuery?: string } = {}) => ({
    link: `/profile/${profileId}${linkQuery}`,
  })),
  forwardRequest: new RouteConstructor((forwardRequestName: string, forwardRequestToken: string) => ({
    link: `/${forwardRequestName}/${forwardRequestToken}`,
  })),
  profileEdit: new RouteConstructor((profileId, editView) => ({
    link: `/profile/${profileId}/${editView}`,
  })),
  profileEditMediaReview: new RouteConstructor((mediaType, mediaIds, profileId, forwardedPage, attachToId, unlock) => {
    const attachToString = attachToId ? `&attachToId=${attachToId}` : '';
    const unlockString = unlock ? '&unlock=true' : '';
    const searchString = `forwardedPage=${forwardedPage}${attachToString}${unlockString}&mediaIds=${mediaIds.join(
      ','
    )}`;

    return {
      link: `/profile/${profileId}/review-media/${mediaType}/?${searchString}`,
    };
  }),
  castingBillboard: new RouteConstructor(({ linkQuery = '' } = {}) => ({
    link: `/casting-billboard${linkQuery}`,
  })),
  castingBillboardSavedSearches: new RouteConstructor(({ linkQuery = '' } = {}) => ({
    link: `/casting-billboard/saved-searches${linkQuery}`,
  })),
  profileSettings: new RouteConstructor((artistId, profileId) => ({
    link: `/profile-settings/talents/${artistId}/profiles/${profileId}`,
  })),
  profileSettingsTalentProfiles: new RouteConstructor((artistId) => ({
    link: `/profile-settings/talents/${artistId}/profiles`,
  })),
  profileSettingsTalents: new RouteConstructor(() => ({
    link: '/profile-settings/talents',
  })),
  membershipOverview: new RouteConstructor((editSuccess?: boolean) => ({
    link: '/membership-billing',
    state: { editSuccess },
  })),
  editBillingInfo: new RouteConstructor(() => ({
    link: '/membership-billing/edit-billing-info',
  })),
  editMembershipSubscriptionSummary: new RouteConstructor((artistId: number | string, planId: string) => ({
    link: `/membership-billing/${artistId}/subscription-summary/${planId}`,
  })),
  editMembershipCreateCheckout: new RouteConstructor((artistId, purchase: PURCHASE) => ({
    link: `/membership-billing/${artistId}/checkout`,
    state: {
      purchase,
    },
  })),
  editMembershipRenewal: new RouteConstructor((artistId, purchase: PURCHASE) => ({
    urlObject: { pathname: '/membership-billing/checkout', query: { artistId } },
    link: `/membership-billing/${artistId}/checkout`,
    state: {
      purchase,
    },
  })),
  editMembershipPurchaseSuccess: new RouteConstructor((artistId) => ({
    link: `/membership-billing/${artistId}/purchase-success`,
  })),
  mediaBank: new RouteConstructor((mediaType?: string) =>
    mediaType
      ? {
          link: `/media-bank/${mediaType}`,
        }
      : {
          link: '/media-bank',
        }
  ),
  verifyEmail: new RouteConstructor(() => ({
    link: '/verify-email',
  })),
  verifyEmailCode: new RouteConstructor((verificationId) => ({
    link: `/verify-email?verificationId=${verificationId}`,
  })),
  accountDetails: new RouteConstructor(() => ({
    link: '/account-settings/details',
  })),
  notificationSettings: new RouteConstructor(() => ({
    link: '/account-settings/notifications',
  })),
  artistNotificationSettings: new RouteConstructor((artistId) => ({
    link: `/account-settings/notifications/${artistId}`,
  })),
  artistProjectsNotificationSettings: new RouteConstructor((artistId) => ({
    link: `/account-settings/notifications/${artistId}/projects`,
  })),
  yourAlerts: new RouteConstructor((queryParams?: URLSearchParams) => ({
    link: `/your-alerts?${queryParams ? queryParams.toString() : ''}`,
  })),
  rosterInvite: new RouteConstructor((rosterInviteToken) => ({
    link: `/roster-invite/${rosterInviteToken}`,
  })),
  forwardedRequest: new RouteConstructor((forwardedRequestData) => ({
    link: `/${forwardedRequestData.type}/${forwardedRequestData.token}`,
  })),
  questionRequest: new RouteConstructor((requestId: string) => ({
    link: `/request/${requestId}`,
  })),
  mediaRequest: new RouteConstructor((requestId: string) => ({
    link: `/media-request/${requestId}`,
  })),
  auditionRequest: new RouteConstructor((requestId: string) => ({
    link: `/audition-invite/${requestId}`,
  })),
  callbackRequest: new RouteConstructor((requestId: string) => ({
    link: `/callback-invite/${requestId}`,
  })),
  defaultRedirect: new RouteConstructor(() => ({
    link: `/default-redirect`,
  })),
  customizeSubmission: new RouteConstructor((projectId: string | number, roleId: string | number) => ({
    link: `/project/${projectId}/role/${roleId}/customize-submission`,
  })),
  submissionCompletion: new RouteConstructor((projectId: string | number, roleId: string | number) => ({
    link: `/project/${projectId}/role/${roleId}/submission-completion`,
  })),
};

type RouteHelperType = typeof routeHelper;
export type RouteHelperRoute = keyof RouteHelperType;
type RouteHelperParameters<Route extends RouteHelperRoute> = Parameters<RouteHelperType[Route]['link']>;

interface GenerateDefaultLinkOpts {
  profileId?: string;
  artistId?: number;
  profileStatusCode?: string;
  isPersonal?: boolean;
  isLoggedIn?: boolean;
  rosterInvitationToken?: string;
  forwardedRequestTokenData?: Record<string, any> | any[];
  artistHasRepProfile?: boolean;
  shouldCreatePersonalProfile?: boolean;
}
type GenerateDefaultLinkFn = (
  options: GenerateDefaultLinkOpts,
  withRedirect?: boolean,
  isFromOnboarding?: boolean
) => string;

export const generateDefaultLink: GenerateDefaultLinkFn = (
  {
    profileId,
    artistId,
    profileStatusCode,
    isPersonal,
    isLoggedIn,
    rosterInvitationToken,
    forwardedRequestTokenData,
    artistHasRepProfile,
    shouldCreatePersonalProfile,
  },
  isFromOnboarding = false
): string => {
  if (isLoggedIn) {
    if (forwardedRequestTokenData) {
      return routeHelper.forwardedRequest.link(forwardedRequestTokenData);
    }

    if (profileId) {
      if (shouldCreatePersonalProfile) {
        removeMarketingRedirect();

        return routeHelper.artistCreatePersonal.link();
      }

      const redirectUrl = getMarketingRedirect();
      const onboardingQuery = getRouterQuery(isFromOnboarding);

      // check the profile status code and redirect to the correct page this is for returning users who likely left
      // the site before completing their profile
      switch (profileStatusCode) {
        case PROFILE_STATUS.VERIFY_AGE:
          return routeHelper.profileCreate.link(profileId);
        case PROFILE_STATUS.CREATE_PROFILE_STEP_1:
          return routeHelper.profileCreate.link(profileId);
        case PROFILE_STATUS.CREATE_PROFILE_STEP_2:
          return routeHelper.profileUploadPhoto.link(profileId);
        case PROFILE_STATUS.CREATE_PROFILE_STEP_3:
          return routeHelper.profileUploadVideo.link(profileId);
        case PROFILE_STATUS.CREATE_PROFILE_STEP_4:
          return routeHelper.profileUploadAudio.link(profileId);
        case PROFILE_STATUS.SELECT_MEMBERSHIP:
          return routeHelper.profileSubscriptions.link(
            profileId,
            onboardingQuery,
            signUpQueryParams.queryParamsSelectMembership
          );
        case PROFILE_STATUS.ACTIVE:
          if (redirectUrl) {
            removeMarketingRedirect();

            return redirectUrl;
          }

          if (artistHasRepProfile) {
            return routeHelper.profile.link(profileId, onboardingQuery);
          }

          return isPersonal
            ? routeHelper.castingBillboard.link(onboardingQuery)
            : routeHelper.profile.link(profileId, onboardingQuery); // I don't think this path can ever be hit, it would be for a non-personal profile on an artist with no repped profiles
        default:
          return routeHelper.profileSettings.link(artistId, profileId);
      }
    } else {
      if (rosterInvitationToken) {
        return routeHelper.rosterInvite.link(rosterInvitationToken);
      }

      return routeHelper.profileCreate.link(null, signUpQueryParams.registrationStepIds.verifyAge);
    }
  }

  return routeHelper.root.link();
};

export default routeHelper;

// this wraps the config currently built into the routeHelper, but when doing a redirect
// it uses the `push` from the current history in the <Router /> context instead of what is
// potentially a stale reference that is baked into the routeHelper as the app loads
export const useRouteHelper = () => {
  const { push: routerPush, location } = useHistory();

  const link = useCallback(<Route extends RouteHelperRoute>(route: Route, ...params: RouteHelperParameters<Route>) => {
    return (routeHelper[route] as RouteConstructor<any[]>).link(...params);
  }, []);
  const push = useCallback(
    <Route extends RouteHelperRoute>(route: Route, ...params: RouteHelperParameters<Route>) => {
      const { link: routeHelperLink, state } = (routeHelper[route] as RouteConstructor<any[]>).build(...params);

      routerPush(routeHelperLink, state);
    },
    [routerPush]
  );

  return useMemo(
    () => ({
      location,
      link,
      push,
      contextPush: routerPush,
    }),
    [location, link, push, routerPush]
  );
};
